(function () {
  'use strict';

  const STORE_URL = 'https://RoomForPlants.com';
  const PROXY_URL = 'https://lively-wind-45c5.calm-dust-fc1b.workers.dev'; 

  let CATALOG = [];
  let CATALOG_BY_HANDLE = {};
  let CATALOG_PROMPT = '';
  let catalogReady = false;
  let history = [];
  let quizAnswers = {};
  let quizStep = 0;
  let quizPath = null;
  let lastFocusedElement = null;
  let assistantUsed = false;

  // ─── EMOJI MAP ───────────────────────────────────────────────────────────────

  const EMOJI_MAP = [
    [['palm', 'areca', 'majesty', 'christmas palm', 'spindle palm'], '🌴'],
    [['fiddle', 'ficus'], '🌿'],
    [['olive'], '🫒'],
    [['monstera', 'philodendron', 'pothos'], '🌱'],
    [['snake', 'sansevieria', 'dracaena'], '🪴'],
    [['succulent', 'agave', 'cactus', 'aloe', 'echeveria'], '🌵'],
    [['rose', 'camellia', 'peony', 'azalea', 'hydrangea'], '🌸'],
    [['lavender', 'salvia'], '💜'],
    [['boxwood', 'topiary', 'arborvitae', 'holly'], '🌲'],
    [['fern', 'grass', 'sedge', 'reed'], '🍃'],
    [['tree', 'oak', 'maple', 'magnolia', 'crape myrtle', 'birch'], '🌳'],
    [['shrub', 'loropetalum', 'viburnum', 'butterfly bush'], '🌲'],
    [['herb', 'basil', 'mint', 'rosemary', 'thyme'], '🌿'],
    [['fruit', 'citrus', 'lemon', 'orange', 'apple', 'fig', 'berry', 'blueberry'], '🍋'],
    [['vegetable', 'tomato', 'pepper', 'squash'], '🥦'],
    [['air plant', 'tillandsia', 'bromeliad'], '🌬️'],
    [['planter', 'pot', 'container', 'fertilizer', 'soil', 'supply', 'accessory'], '🪣'],
    [['tropical', 'banana', 'bird of paradise', 'heliconia'], '🌺'],
    [['vine', 'jasmine', 'wisteria', 'climbing'], '🌿'],
    [['plant'], '🪴'],
  ];

  function emojiFor(name) {
    const lower = name.toLowerCase();
    for (const [words, emoji] of EMOJI_MAP) {
      if (words.some(w => lower.includes(w))) return emoji;
    }
    return '🌿';
  }

  // ─── TAG INFERENCE ────────────────────────────────────────────────────────────

  function tagsFrom(product) {
    const tags = (product.tags || []).map(t => t.toLowerCase());
    const title = product.title.toLowerCase();
    const type = (product.product_type || '').toLowerCase();
    const inferred = [];

    // Indoor vs outdoor
    const hasIndoorTag = tags.some(t => t.includes('indoors'));
    const hasOutdoorTag = tags.some(t => t.includes('outdoors'));
    if (hasIndoorTag || /houseplant|indoor|ficus|monstera|philodendron|pothos|dracaena|snake plant|sansevieria|zz plant|peace lily|bromeliad|air plant/.test(title + ' ' + type)) {
      inferred.push('indoor');
    }
    if (hasOutdoorTag || /shrub|tree|perennial|rose|camellia|azalea|hydrangea|grass|fern|lavender|arborvitae|boxwood|holly|magnolia|crape myrtle|drift rose/.test(title)) {
      inferred.push('outdoor');
    }

    // Light needs
    const sunTag = tags.find(t => t.startsWith('sunlight value:'));
    if (sunTag) {
      const sunVal = sunTag.split(':')[1].trim();
      if (/full sun/.test(sunVal)) inferred.push('full-sun');
      if (/part sun|full-part/.test(sunVal)) inferred.push('part-sun');
      if (/indirect|shade/.test(sunVal)) inferred.push('low-light', 'shade');
    }

    // Size/placement
    const heightTag = tags.find(t => t.startsWith('mature height value:'));
    if (heightTag) {
      const h = heightTag.split(':')[1].trim();
      const ft = parseFloat(h);
      if (!isNaN(ft)) {
        if (ft <= 1.5) inferred.push('small', 'desk', 'tabletop');
        else if (ft <= 4) inferred.push('medium', 'patio', 'counter');
        else if (ft <= 10) inferred.push('large', 'floor', 'statement');
        else inferred.push('tree', 'large', 'statement');
      }
    }

    // Category from tags
    const catTag = tags.find(t => t.startsWith('category:'));
    if (catTag) inferred.push(catTag.split(':')[1].trim());

    const subcatTag = tags.find(t => t.startsWith('subcategory:'));
    if (subcatTag) inferred.push(subcatTag.split(':')[1].trim());

    // Title-based
    if (/succulent|cactus|agave|aloe/.test(title)) inferred.push('drought-tolerant', 'easy-care');
    if (/fern|shade/.test(title)) inferred.push('shade', 'low-light');
    if (/rose|camellia|hydrangea|azalea/.test(title)) inferred.push('flowering', 'blooms');
    if (/palm|tropical|banana|bird of paradise/.test(title)) inferred.push('tropical', 'statement');
    if (/herb|basil|mint|rosemary|thyme|oregano/.test(title)) inferred.push('edible', 'kitchen', 'herb');
    if (/fruit|berry|apple|pear|citrus|lemon|fig|plum/.test(title)) inferred.push('edible', 'fruit');
    if (/air plant|tillandsia/.test(title)) inferred.push('no-soil', 'easy-care', 'desk');
    if (/planter|pot|fertilizer|soil|supply|mitogrow/.test(title)) inferred.push('supply', 'accessory');
    if (/gift|arrangement/.test(title)) inferred.push('gift');

    return [...new Set([...tags, ...inferred])];
  }

  // ─── CATALOG FETCH ────────────────────────────────────────────────────────────

  async function fetchCatalog() {
    try {
      let page = 1;
      let allProducts = [];

      while (true) {
        const res = await fetch(`${STORE_URL}/products.json?limit=250&page=${page}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        const batch = data.products || [];
        allProducts = allProducts.concat(batch);

        if (batch.length < 250) break;
        page++;
      }

      if (!allProducts.length) throw new Error('empty');
      buildCatalog(allProducts);
    } catch (e) {
      useFallbackCatalog();
    }

    catalogReady = true;
  }

  function money(value) {
    const n = parseFloat(value || 0);
    return '$' + n.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }

  function formatProductPrice(product) {
    const variants = product.variants || [];

    const prices = variants
      .map(v => parseFloat(v.price))
      .filter(n => !isNaN(n));

    const comparePrices = variants
      .map(v => parseFloat(v.compare_at_price))
      .filter(n => !isNaN(n) && n > 0);

    if (!prices.length) return '';

    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const compareMax = comparePrices.length ? Math.max(...comparePrices) : null;

    if (min !== max) {
      return `<span class="rfp-product-sale-price">${money(min)} – ${money(max)}</span>`;
    }

    if (compareMax && compareMax > min) {
      return `
        <span class="rfp-product-sale-price">${money(min)}</span>
        <span class="rfp-product-compare-price">${money(compareMax)}</span>
      `;
    }

    return `<span class="rfp-product-sale-price">${money(min)}</span>`;
  }

  function buildCatalog(products) {
    CATALOG = products
      .filter(p => p.variants?.length && p.published_at)
      .map(p => {
        const v = p.variants[0];
        const priceRaw = parseFloat(v.price);

        return {
          handle: p.handle,
          name: p.title,
          price: formatProductPrice(p),
          priceRaw,
          variantId: String(v.id),
          image: p.images?.[0]?.src || null,
          emoji: emojiFor(p.title),
          tags: tagsFrom(p),
          variants: p.variants.map(v2 => ({
            id: String(v2.id),
            title: v2.title,
            price: '$' + parseFloat(v2.price).toLocaleString()
          })),
        };
      });

    CATALOG_BY_HANDLE = {};
    CATALOG.forEach(p => { CATALOG_BY_HANDLE[p.handle] = p; });

    CATALOG_PROMPT = CATALOG
      .map(p => `${p.name} | ${p.handle} | ${p.price} | ${p.tags.slice(0, 8).join(',')}`)
      .join('\n');
  }

  function useFallbackCatalog() {
    const fallback = [
      { handle: 'fiddle-leaf-fig-tree', title: 'Fiddle Leaf Fig Tree', variants: [{ id: '1', price: '299.95' }], images: [], tags: [], published_at: '2024-01-01' },
      { handle: 'areca-palm-tree', title: 'Areca Palm Tree', variants: [{ id: '2', price: '299.95' }], images: [], tags: [], published_at: '2024-01-01' },
      { handle: 'monstera-deliciosa', title: 'Monstera Deliciosa', variants: [{ id: '3', price: '89.95' }], images: [], tags: [], published_at: '2024-01-01' },
      { handle: 'snake-plant', title: 'Snake Plant', variants: [{ id: '4', price: '49.95' }], images: [], tags: [], published_at: '2024-01-01' },
      { handle: 'japanese-painted-fern', title: 'Japanese Painted Fern', variants: [{ id: '5', price: '59.95' }], images: [], tags: [], published_at: '2024-01-01' },
      { handle: 'popcorn-drift-rose', title: 'Popcorn Drift® Rose', variants: [{ id: '6', price: '99.95' }], images: [], tags: [], published_at: '2024-01-01' },
      { handle: 'majesty-palm-tree', title: 'Majesty Palm Tree', variants: [{ id: '7', price: '199.95' }], images: [], tags: [], published_at: '2024-01-01' },
      { handle: 'greensboro-red-camellia', title: 'Greensboro Red Camellia', variants: [{ id: '8', price: '119.95' }], images: [], tags: [], published_at: '2024-01-01' },
    ];
    buildCatalog(fallback);
  }

  // ─── QUIZ FLOWS ───────────────────────────────────────────────────────────────

  const PATH_STEP = {
    label: "What can I help you find?",
    key: "path",
    choices: [
      { icon: "🔍", label: "Find the right plant for me", value: "find" },
      { icon: "🏠", label: "Help me style a space", value: "style" },
      { icon: "🌿", label: "Build an outdoor garden", value: "outdoor" },
      { icon: "🎁", label: "I'm shopping for a gift", value: "gift" },
    ]
  };

  const QUIZ_FLOWS = {
    find: [
      {
        label: "Where will it live?", key: "location", choices: [
          { icon: "🛋️", label: "Inside — living room or office", value: "indoors in a living room or office" },
          { icon: "🛏️", label: "Inside — bedroom or bathroom", value: "indoors in a bedroom or bathroom" },
          { icon: "🌤️", label: "Outside — patio or balcony", value: "on a patio or balcony" },
          { icon: "🌳", label: "Outside — yard or garden", value: "in an outdoor yard or garden bed" },
        ]
      },
      {
        label: "How's the light?", key: "light", choices: [
          { icon: "☀️", label: "Bright — lots of direct sun", value: "bright direct sunlight" },
          { icon: "🌤️", label: "Bright but indirect", value: "bright indirect light" },
          { icon: "🌥️", label: "Medium — decent but not sunny", value: "medium indirect light" },
          { icon: "🌑", label: "Low — not much natural light", value: "low light" },
          { icon: "🤷", label: "Not sure", value: "unsure about light levels" },
        ]
      },
      {
        label: "How involved do you want to be?", key: "care", choices: [
          { icon: "🌱", label: "Very easy — hard to kill, please", value: "very low maintenance, nearly impossible to kill" },
          { icon: "🪴", label: "A little care is fine", value: "moderate care, willing to water and occasionally fertilize" },
          { icon: "🌸", label: "I enjoy tending to plants", value: "higher maintenance is welcome, I enjoy caring for plants" },
        ]
      },
      {
        label: "What size are you thinking?", key: "size", choices: [
          { icon: "🌱", label: "Small — desk, shelf, or tabletop", value: "small, for a desk or shelf" },
          { icon: "🪴", label: "Medium — counter or side table", value: "medium, for a table or counter" },
          { icon: "🌳", label: "Large — floor plant or corner statement", value: "large floor plant or corner statement" },
          { icon: "🤷", label: "Open to anything", value: "any size" },
        ]
      },
      {
        label: "What's your budget?", key: "budget", choices: [
          { icon: "💚", label: "Under $75", value: "under $75" },
          { icon: "💛", label: "Under $200", value: "under $200" },
          { icon: "🧡", label: "Under $500", value: "under $500" },
          { icon: "❤️", label: "No limit", value: "any budget" },
        ]
      },
    ],

    style: [
      {
        label: "Which room are we working with?", key: "room", choices: [
          { icon: "🛋️", label: "Living room", value: "living room" },
          { icon: "🍳", label: "Kitchen or dining room", value: "kitchen or dining room" },
          { icon: "🛏️", label: "Bedroom", value: "bedroom" },
          { icon: "🚪", label: "Entryway or hallway", value: "entryway or hallway" },
          { icon: "💼", label: "Home office", value: "home office" },
        ]
      },
      {
        label: "How much light does the room get?", key: "light", choices: [
          { icon: "☀️", label: "Very bright — sunny windows", value: "lots of direct sun" },
          { icon: "🌤️", label: "Bright but indirect", value: "bright indirect light" },
          { icon: "🌥️", label: "Medium — decent but shaded", value: "medium light" },
          { icon: "🌑", label: "Low — pretty dim", value: "low light" },
        ]
      },
      {
        label: "What's the vibe?", key: "style", choices: [
          { icon: "⬜", label: "Modern & minimal", value: "modern and minimal" },
          { icon: "🪵", label: "Warm & natural", value: "warm and natural" },
          { icon: "🌊", label: "Coastal & breezy", value: "coastal and relaxed" },
          { icon: "🌸", label: "Romantic & lush", value: "romantic and lush" },
          { icon: "🏛️", label: "Classic & traditional", value: "classic and traditional" },
        ]
      },
      {
        label: "What's your budget?", key: "budget", choices: [
          { icon: "💚", label: "Under $100", value: "under $100" },
          { icon: "💛", label: "Under $300", value: "under $300" },
          { icon: "🧡", label: "Under $600", value: "under $600" },
          { icon: "❤️", label: "No limit", value: "any budget" },
        ]
      },
    ],

    outdoor: [
      {
        label: "What kind of outdoor space?", key: "space", choices: [
          { icon: "🌿", label: "Garden bed or border", value: "garden bed or border" },
          { icon: "🌳", label: "Yard or lawn", value: "yard or lawn" },
          { icon: "🏡", label: "Patio or deck", value: "patio or deck in containers" },
          { icon: "🌆", label: "Balcony", value: "balcony in containers" },
        ]
      },
      {
        label: "What are your sun conditions?", key: "light", choices: [
          { icon: "☀️", label: "Full sun — 6+ hours direct", value: "full sun" },
          { icon: "⛅", label: "Part sun — 3–6 hours", value: "part sun or dappled light" },
          { icon: "🌑", label: "Shade — mostly shaded", value: "shade" },
          { icon: "🤷", label: "Mix — varies around the yard", value: "mixed sun conditions" },
        ]
      },
      {
        label: "What are you hoping to plant?", key: "goal", choices: [
          { icon: "🌸", label: "Color and blooms", value: "flowering plants and colorful blooms" },
          { icon: "🌲", label: "Privacy or structure", value: "shrubs or trees for privacy or structure" },
          { icon: "🍋", label: "Edibles — fruit, veg, or herbs", value: "edible plants, fruits, vegetables, or herbs" },
          { icon: "🍃", label: "Greenery and texture", value: "foliage, texture, and greenery" },
        ]
      },
      {
        label: "What's your budget?", key: "budget", choices: [
          { icon: "💚", label: "Under $100", value: "under $100" },
          { icon: "💛", label: "Under $300", value: "under $300" },
          { icon: "🧡", label: "Under $600", value: "under $600" },
          { icon: "❤️", label: "No limit", value: "any budget" },
        ]
      },
    ],

    gift: [
      {
        label: "What's the occasion?", key: "occasion", choices: [
          { icon: "🏡", label: "Housewarming", value: "housewarming" },
          { icon: "🎂", label: "Birthday", value: "birthday" },
          { icon: "💐", label: "Sympathy", value: "sympathy" },
          { icon: "🙏", label: "Thank you", value: "thank you" },
          { icon: "💼", label: "Corporate", value: "corporate gift" },
        ]
      },
      {
        label: "Where does the recipient live?", key: "location", choices: [
          { icon: "🏙️", label: "Apartment or condo", value: "apartment or condo, likely indoors" },
          { icon: "🏡", label: "House with a yard", value: "house with outdoor space" },
          { icon: "🤷", label: "Not sure", value: "unknown living situation" },
        ]
      },
      {
        label: "What's your budget?", key: "budget", choices: [
          { icon: "💚", label: "Under $75", value: "under $75" },
          { icon: "💛", label: "Under $150", value: "under $150" },
          { icon: "🧡", label: "Under $300", value: "under $300" },
          { icon: "❤️", label: "No limit", value: "any budget" },
        ]
      },
      {
        label: "How green is their thumb?", key: "experience", choices: [
          { icon: "🌱", label: "Beginner — keep it easy", value: "beginner, easy-care plants only" },
          { icon: "🪴", label: "Comfortable with plants", value: "comfortable with basic plant care" },
          { icon: "🌳", label: "Plant lover — they'll enjoy the challenge", value: "experienced plant lover" },
        ]
      },
    ],
  };

  // ─── SYSTEM PROMPT ────────────────────────────────────────────────────────────

  function buildSystemPrompt() {
    return `You are the RoomForPlants Plant Advisor — a warm, knowledgeable guide for RoomForPlants.com, a U.S. online plant retailer offering a wide selection of live plants, trees, shrubs, edibles, perennials, houseplants, and garden supplies delivered nationwide.

LIVE PRODUCT CATALOG (${CATALOG.length} products — always recommend only from this list, using the exact handle):
${CATALOG_PROMPT}

You receive structured answers from customers who want help finding the right plant. Based on their answers, recommend either 1, 2, or 4 products — never 3.
- Recommend 2 by default.
- Recommend 4 when they're building a garden, styling a full space, or seem open to a collection.
- Recommend 1 only when there's one obvious perfect match.

Be warm, specific, and practical. Connect each recommendation directly to what they told you — their light, care preferences, space, or occasion.

KEY BRAND FACTS:
- RoomForPlants sells live plants shipped nationwide across the U.S.
- Products include indoor houseplants, outdoor trees, shrubs, perennials, edibles (fruits, vegetables, herbs), air plants, succulents, cacti, and garden supplies.
- Light requirements vary by plant — always factor in what the customer told you about their light conditions.
- Care level matters — match plant difficulty to the customer's experience and availability.
- Growing zones matter for outdoor plants — the catalog includes zone information in tags. If a customer mentions their location or zone, factor it in. If they don't, recommend hardy or widely-adaptable options and note zone considerations.
- Shipping: live plants ship with care. Most orders arrive within 1–2 weeks. Fragile or large plants may have longer lead times.
- If they ask about returns, care after delivery, or anything operational, direct them to hello@roomforplants.com for the most accurate answer.

BRAND VOICE — speak like this:
- Knowledgeable but not intimidating
- Warm and welcoming — beginners feel comfortable here
- Light and conversational — occasional warmth and wit, but never forced
- Specific and helpful — never vague

Instead of "Low-maintenance plant" say "Looks great without asking for much."
Instead of "Requires bright indirect light" say "Give it a bright spot away from harsh direct sun and it'll be happy."
Instead of "Suitable for beginners" say "A great choice if you're just getting started — or if you'd simply like a plant that's easy to live with."

RECOMMENDATION FORMAT — always end with this exact block, nothing after it:
<products>
[{"handle":"exact-handle-from-catalog","reason":"One sentence tied directly to their answers"}]
</products>

RULES:
- Only recommend products from the catalog using exact handles.
- 2–3 sentences max before the products block. Keep it warm and specific.
- No bullet lists, no multiple questions in one reply.
- Always factor in light conditions — unlike faux plants, live plants absolutely need the right light.
- If they ask to speak to a person or customer service, tell them to email hello@roomforplants.com — they'll hear back within 1 business day.
- Stay on-topic. If they ask something unrelated to plants or RoomForPlants, politely let them know you're here to help with plants.
- If they type a freeform answer instead of tapping a button, honor it exactly.`;
  }

  // ─── DOM UTILITIES ────────────────────────────────────────────────────────────

  const linkIcon = `<svg aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>`;

  function getFocusable(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(el => el.offsetParent !== null || el === document.activeElement);
  }

  function focusFirstInPopup() {
    const popup = document.getElementById('rfp-popup');
    const focusables = getFocusable(popup);
    if (focusables.length) focusables[0].focus();
  }

  function handleDialogKeydown(e) {
    const popup = document.getElementById('rfp-popup');
    if (!popup || !popup.classList.contains('open')) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      closePopup();
      return;
    }

    if (e.key !== 'Tab') return;

    const focusables = getFocusable(popup);
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function setPopupA11y(isOpen) {
    const popup = document.getElementById('rfp-popup');
    const backdrop = document.getElementById('rfp-backdrop');

    if (popup) {
      popup.setAttribute('role', 'dialog');
      popup.setAttribute('aria-modal', 'true');
      popup.setAttribute('aria-labelledby', 'rfp-quiz-header-question');
      popup.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    }
    if (backdrop) {
      backdrop.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    }
  }

  // ─── POPUP OPEN / CLOSE ───────────────────────────────────────────────────────

  function openPopup(initialPath) {
    lastFocusedElement = document.activeElement;
    assistantUsed = true;
    logAssistantEvent('assistant_opened');

    const backdrop = document.getElementById('rfp-backdrop');
    const popup = document.getElementById('rfp-popup');

    if (backdrop) backdrop.classList.add('open');
    if (popup) popup.classList.add('open');

    setPopupA11y(true);
    document.body.style.overflow = 'hidden';

    resetQuiz();

    if (initialPath) {
      quizPath = initialPath;
      quizStep = 0;
      renderQuizStep();
    } else {
      renderPathStep();
    }

    document.addEventListener('keydown', handleDialogKeydown);
    setTimeout(focusFirstInPopup, 60);
  }

  function closePopup() {
    const backdrop = document.getElementById('rfp-backdrop');
    const popup = document.getElementById('rfp-popup');

    if (backdrop) backdrop.classList.remove('open');
    if (popup) popup.classList.remove('open');

    setPopupA11y(false);
    document.body.style.overflow = '';
    document.removeEventListener('keydown', handleDialogKeydown);

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      setTimeout(() => lastFocusedElement.focus(), 30);
    }
  }

  function resetQuiz() {
    quizAnswers = {};
    quizStep = 0;
    quizPath = null;
    history = [];

    const body = document.getElementById('rfp-quiz-body');
    if (body) body.innerHTML = '';
  }

  // ─── RENDER STEPS ─────────────────────────────────────────────────────────────

  function renderPathStep() {
    updateProgress(0, 1);
    renderStep(PATH_STEP, 0, 1, (value) => {
      quizPath = value;
      quizStep = 0;
      renderQuizStep();
    });
  }

  function renderQuizStep() {
    const flow = QUIZ_FLOWS[quizPath];
    if (!flow) {
      renderPathStep();
      return;
    }

    const total = flow.length;
    const step = flow[quizStep];

    updateProgress(quizStep, total);

    renderStep(step, quizStep, total, (value, label) => {
      quizAnswers[step.key] = label;

      logAssistantEvent('quiz_answer', {
        step: step.key,
        answer: label
      });

      quizStep++;

      if (quizStep >= total) {
        submitQuiz();
      } else {
        renderQuizStep();
      }
    });
  }

  function updateProgress(current, total) {
    const bar = document.getElementById('rfp-progress-bar');
    if (!bar) return;

    bar.innerHTML = '';
    bar.setAttribute('role', 'progressbar');
    bar.setAttribute('aria-valuemin', '1');
    bar.setAttribute('aria-valuemax', String(total));
    bar.setAttribute('aria-valuenow', String(current + 1));
    bar.setAttribute('aria-label', `Quiz step ${current + 1} of ${total}`);

    for (let i = 0; i < total; i++) {
      const seg = document.createElement('div');
      seg.className = 'rfp-prog-seg' + (i <= current ? ' done' : '');
      seg.setAttribute('aria-hidden', 'true');
      bar.appendChild(seg);
    }
  }

  function renderStep(step, stepIdx, total, onChoose) {
    const header = document.getElementById('rfp-quiz-header-question');
    const stepLabel = document.getElementById('rfp-quiz-step-label');
    const body = document.getElementById('rfp-quiz-body');

    if (!body) return;

    if (header) header.textContent = step.label;
    if (stepLabel) stepLabel.textContent = `Step ${stepIdx + 1} of ${total}`;

    body.innerHTML = '';

    const choicesEl = document.createElement('div');
    choicesEl.className = 'rfp-choices';
    choicesEl.setAttribute('role', 'group');
    choicesEl.setAttribute('aria-label', step.label);

    step.choices.forEach(c => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rfp-choice';
      btn.setAttribute('aria-label', c.label);
      btn.innerHTML = `<span class="rfp-choice-icon" aria-hidden="true">${c.icon}</span><span class="rfp-choice-label">${c.label}</span>`;

      btn.addEventListener('click', () => {
        choicesEl.querySelectorAll('.rfp-choice').forEach(b => {
          b.classList.remove('sel');
          b.removeAttribute('aria-pressed');
        });
        btn.classList.add('sel');
        btn.setAttribute('aria-pressed', 'true');
        setTimeout(() => onChoose(c.value, c.label), 220);
      });

      choicesEl.appendChild(btn);
    });

    body.appendChild(choicesEl);

    const isPathStep = step.key === 'path';

    const fallback = document.createElement('div');
    fallback.className = 'rfp-fallback';
    fallback.innerHTML = `
      <div class="rfp-fallback-row">
        <label class="visually-hidden" for="rfp-fallback-input">
          ${isPathStep ? 'Ask a question instead of taking the quiz' : 'Describe your answer in your own words'}
        </label>
        <input
          class="rfp-fallback-input"
          id="rfp-fallback-input"
          type="text"
          placeholder="${isPathStep ? 'Have a question? Just ask…' : 'Or describe in your own words…'}"
          autocomplete="off"
        >
        <button
          class="rfp-fallback-btn"
          id="rfp-fallback-btn"
          type="button"
          aria-label="${isPathStep ? 'Ask question' : 'Submit custom answer'}"
        >→</button>
      </div>
    `;

    body.appendChild(fallback);

    const footnote = document.createElement('p');
    footnote.className = 'rfp-ai-footnote';
    footnote.textContent = 'Personalized recommendations powered by AI. Intended as general guidance.';
    body.appendChild(footnote);

    const inp = fallback.querySelector('#rfp-fallback-input');
    const fbBtn = fallback.querySelector('#rfp-fallback-btn');

    const submitFallback = async () => {
      const val = inp.value.trim();
      if (!val) return;

      logAssistantEvent('user_message', { message: val });

      if (isPathStep) {
        showResultsLoading();
        history = [{ role: 'user', content: val }];

        try {
          const raw = await callClaude(history);
          history.push({ role: 'assistant', content: raw });
          const { clean, recs } = parseProducts(raw);
          showResults(clean, recs);
        } catch (e) {
          showResultsError();
        }
        return;
      }

      onChoose(val, val);
    };

    fbBtn.addEventListener('click', submitFallback);
    inp.addEventListener('keydown', e => { if (e.key === 'Enter') submitFallback(); });

    setTimeout(() => {
      const firstChoice = choicesEl.querySelector('.rfp-choice');
      if (firstChoice) firstChoice.focus();
    }, 30);
  }

  // ─── QUIZ SUBMIT ──────────────────────────────────────────────────────────────

  async function submitQuiz() {
    const pathLabels = {
      find: 'find the right plant',
      style: 'style a room with plants',
      outdoor: 'build an outdoor garden',
      gift: 'find a plant as a gift',
    };

    const lines = [`The customer wants to ${pathLabels[quizPath] || quizPath}.`];
    Object.entries(quizAnswers).forEach(([key, val]) => {
      lines.push(`${key}: ${val}`);
    });

    const prompt = lines.join('\n');
    showResultsLoading();

    history = [{ role: 'user', content: prompt }];

    try {
      const raw = await callClaude(history);
      history.push({ role: 'assistant', content: raw });
      const { clean, recs } = parseProducts(raw);
      showResults(clean, recs);
    } catch (e) {
      showResultsError();
    }
  }

  // ─── RESULTS STATES ───────────────────────────────────────────────────────────

  function showResultsLoading() {
    const body = document.getElementById('rfp-quiz-body');
    const header = document.getElementById('rfp-quiz-header-question');
    const stepLabel = document.getElementById('rfp-quiz-step-label');

    if (header) header.textContent = 'One moment…';
    if (stepLabel) stepLabel.textContent = '';

    if (body) {
      body.innerHTML = `
        <div class="rfp-loading" role="status" aria-live="polite">
          <div class="rfp-dots" aria-hidden="true"><span></span><span></span><span></span></div>
          <p class="rfp-loading-text">Finding your perfect plants…</p>
        </div>
      `;
    }

    const flow = QUIZ_FLOWS[quizPath];
    if (flow) updateProgress(flow.length - 1, flow.length);

    const bar = document.getElementById('rfp-progress-bar');
    if (bar) bar.querySelectorAll('.rfp-prog-seg').forEach(s => s.classList.add('done'));
  }

  function showResultsError() {
    const body = document.getElementById('rfp-quiz-body');
    if (!body) return;

    body.innerHTML = '';

    const p = document.createElement('p');
    p.className = 'rfp-error-msg';
    p.textContent = 'Something went wrong. ';

    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'rfp-retry-btn';
    retry.textContent = 'Try again';
    retry.addEventListener('click', () => {
      resetQuiz();
      renderPathStep();
    });

    p.appendChild(retry);
    body.appendChild(p);
    retry.focus();
  }

  function showResults(text, recs) {
    const header = document.getElementById('rfp-quiz-header-question');
    const stepLabel = document.getElementById('rfp-quiz-step-label');
    const body = document.getElementById('rfp-quiz-body');

    if (!body) return;

    if (header) header.textContent = "Here's what I'd suggest";
    if (stepLabel) stepLabel.textContent = '';

    body.innerHTML = '';

    logAssistantEvent('recommendations_shown', {
      handles: (recs || []).map(r => r.handle).join(','),
      recommendation_count: (recs || []).length
    });

    if (text) {
      const p = document.createElement('p');
      p.className = 'rfp-result-text';
      p.textContent = text;
      body.appendChild(p);
    }

  if (recs && recs.length > 0) {
  const cards = makeProductCards(recs);
  if (cards) body.appendChild(cards);
} else {
  const fallback = document.createElement('div');
  fallback.className = 'rfp-no-results';
  fallback.innerHTML = `
    <p>I wasn't able to find an exact match, but you might find something suitable by browsing directly — <a href="${STORE_URL}/collections/all" target="_blank" rel="noopener">explore all plants</a>, or feel free to ask me something different and I'll try another angle.</p>
  `;
  body.appendChild(fallback);
}

    // Follow-up input
    const followUp = document.createElement('div');
    followUp.className = 'rfp-fallback rfp-followup';
    followUp.innerHTML = `
      <div class="rfp-fallback-row">
        <label class="visually-hidden" for="rfp-followup-input">Ask a follow-up question</label>
        <input class="rfp-fallback-input" id="rfp-followup-input" type="text" placeholder="Have a follow-up question? Ask away…" autocomplete="off">
        <button class="rfp-fallback-btn" id="rfp-followup-btn" type="button" aria-label="Submit follow-up question">→</button>
      </div>
    `;
    body.appendChild(followUp);

    const inp = followUp.querySelector('#rfp-followup-input');
    const btn = followUp.querySelector('#rfp-followup-btn');

    const submitFollowup = async () => {
      const val = inp.value.trim();
      if (!val) return;

      logAssistantEvent('user_message', { message: val });
      inp.value = '';
      btn.disabled = true;

      history.push({ role: 'user', content: val });

      const userEl = document.createElement('p');
      userEl.className = 'rfp-chat-user';
      userEl.textContent = val;
      body.insertBefore(userEl, followUp);

      const typingEl = document.createElement('p');
      typingEl.className = 'rfp-chat-assistant rfp-typing-inline';
      typingEl.setAttribute('role', 'status');
      typingEl.setAttribute('aria-live', 'polite');
      typingEl.textContent = 'Thinking…';
      body.insertBefore(typingEl, followUp);
      body.scrollTop = body.scrollHeight;

      try {
        const raw = await callClaude(history);
        history.push({ role: 'assistant', content: raw });

        const { clean, recs: newRecs } = parseProducts(raw);
        typingEl.textContent = clean;
        typingEl.classList.remove('rfp-typing-inline');

      if (newRecs && newRecs.length > 0) {
  const newCards = makeProductCards(newRecs);
  if (newCards) body.insertBefore(newCards, followUp);
} else if (newRecs) {
  const fallback = document.createElement('div');
  fallback.className = 'rfp-no-results';
  fallback.innerHTML = `<p>I wasn't able to find an exact match, but you might find something suitable by browsing directly — <a href="${STORE_URL}/collections/all" target="_blank" rel="noopener">explore all plants</a>, or feel free to ask me something different and I'll try another angle.</p>`;
  body.insertBefore(fallback, followUp);
}
      } catch (e) {
        typingEl.textContent = 'Something went wrong. Please try again.';
      }

      btn.disabled = false;
      body.scrollTop = body.scrollHeight;
      inp.focus();
    };

    btn.addEventListener('click', submitFollowup);
    inp.addEventListener('keydown', e => { if (e.key === 'Enter') submitFollowup(); });

    // Restart button
    const restart = document.createElement('button');
    restart.type = 'button';
    restart.className = 'rfp-restart-btn';
    restart.textContent = '← Start over';
    restart.addEventListener('click', () => {
      resetQuiz();
      renderPathStep();
    });
    body.appendChild(restart);

    setTimeout(() => {
      const firstLinkOrButton = body.querySelector('a, button, input');
      if (firstLinkOrButton) firstLinkOrButton.focus();
    }, 30);
  }

  // ─── PRODUCT CARDS ────────────────────────────────────────────────────────────

  function thumbHTML(p) {
    if (p.image) {
      return `<img src="${p.image}" alt="${p.name}" loading="lazy" onerror="this.style.display='none';this.parentNode.innerHTML='<span aria-hidden=\\"true\\">${p.emoji}</span>'">`;
    }
    return `<span aria-hidden="true">${p.emoji}</span>`;
  }

  function makeProductCards(recs) {
    const products = recs.map(r => {
      const p = CATALOG_BY_HANDLE[r.handle];
      return p ? { ...p, reason: r.reason } : null;
    }).filter(Boolean);

    if (!products.length) return null;

    const wrap = document.createElement('div');
    wrap.className = 'rfp-product-cards';

    products.forEach(p => {
      const card = document.createElement('div');
      card.className = 'rfp-product-card';

      card.innerHTML = `
        <div class="rfp-product-card-top">
          <div class="rfp-product-thumb">${thumbHTML(p)}</div>
          <div class="rfp-product-meta">
            <div class="rfp-product-name">${p.name}</div>
            <div class="rfp-product-reason">${p.reason}</div>
            <div class="rfp-product-price">${p.price}</div>
          </div>
        </div>
        <div class="rfp-product-actions"></div>
      `;

      const actions = card.querySelector('.rfp-product-actions');

      const viewBtn = document.createElement('button');
      viewBtn.type = 'button';
      viewBtn.className = 'rfp-card-btn view-btn';
      viewBtn.innerHTML = `${linkIcon} <span>View plant</span>`;
      viewBtn.addEventListener('click', () => {
        logAssistantEvent('product_clicked', {
          handle: p.handle,
          product_name: p.name
        });
        window.open(`${STORE_URL}/products/${p.handle}`, '_blank', 'noopener');
      });

      actions.appendChild(viewBtn);
      wrap.appendChild(card);
    });

    return wrap;
  }

  // ─── HELPERS ──────────────────────────────────────────────────────────────────

  function parseProducts(text) {
    const match = text.match(/<products>([\s\S]*?)<\/products>/);
    if (!match) return { clean: text, recs: null };

    try {
      const recs = JSON.parse(match[1].trim());
      const clean = text.replace(/<products>[\s\S]*?<\/products>/, '').trim();
      return { clean, recs };
    } catch (e) {
      return { clean: text, recs: null };
    }
  }

  async function callClaude(messages) {
    const res = await fetch(PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-6',
        max_tokens: 1000,
        system: buildSystemPrompt(),
        messages
      }),
    });

    if (!res.ok) throw new Error(`${res.status}`);

    const data = await res.json();
    return data.content?.[0]?.text || 'Something went wrong. Please try again.';
  }

  // ─── ANALYTICS ───────────────────────────────────────────────────────────────

  const LOG_URL = 'https://script.google.com/macros/s/YOUR_LOGGING_SCRIPT_ID/exec'; // update this

  function getAssistantSessionId() {
    let id = localStorage.getItem('rfp_assistant_session');
    if (!id) {
      id = Math.random().toString(36).slice(2) + Date.now();
      localStorage.setItem('rfp_assistant_session', id);
    }
    return id;
  }

  function logAssistantEvent(eventType, data = {}) {
    const passiveEvents = ['add_to_cart_clicked', 'checkout_clicked'];
    if (passiveEvents.includes(eventType) && !assistantUsed) return;

    fetch(LOG_URL, {
      method: 'POST',
      mode: 'no-cors',
      body: JSON.stringify({
        timestamp: new Date().toISOString(),
        session_id: getAssistantSessionId(),
        page_url: window.location.href,
        event_type: eventType,
        ...data
      })
    }).catch(() => {});
  }

  // ─── A11Y DEFAULTS ────────────────────────────────────────────────────────────

  function initA11yDefaults() {
    const ctaBtn = document.getElementById('rfp-cta-btn');
    const closeBtn = document.getElementById('rfp-close-btn');
    const popup = document.getElementById('rfp-popup');
    const backdrop = document.getElementById('rfp-backdrop');
    const toast = document.getElementById('rfp-toast');

    if (ctaBtn) ctaBtn.type = 'button';
    if (closeBtn) {
      closeBtn.type = 'button';
      closeBtn.setAttribute('aria-label', 'Close plant finder');
    }
    if (popup) {
      popup.setAttribute('role', 'dialog');
      popup.setAttribute('aria-modal', 'true');
      popup.setAttribute('aria-labelledby', 'rfp-quiz-header-question');
      popup.setAttribute('aria-hidden', 'true');
    }
    if (backdrop) backdrop.setAttribute('aria-hidden', 'true');
    if (toast) {
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
    }
  }

  // ─── INIT ─────────────────────────────────────────────────────────────────────

  function init() {
    initA11yDefaults();

    const ctaBtn = document.getElementById('rfp-cta-btn');
    if (ctaBtn) ctaBtn.addEventListener('click', () => openPopup(null));

    document.querySelectorAll('.rfp-pill, .rfp-path-btn').forEach(btn => {
      btn.type = 'button';
      btn.addEventListener('click', () => openPopup(btn.dataset.path));
    });

    const closeBtn = document.getElementById('rfp-close-btn');
    if (closeBtn) closeBtn.addEventListener('click', closePopup);

    const backdrop = document.getElementById('rfp-backdrop');
    if (backdrop) backdrop.addEventListener('click', closePopup);

    // Passive e-commerce event tracking
    document.addEventListener('click', function (e) {
      const el = e.target.closest('button, a, input[type="submit"], [role="button"], .shopify-payment-button__button');
      if (!el) return;

      const text = (
        el.innerText || el.textContent || el.value ||
        el.getAttribute('aria-label') || el.getAttribute('data-testid') || ''
      ).toLowerCase();

      const href = (el.getAttribute('href') || '').toLowerCase();
      const name = (el.getAttribute('name') || '').toLowerCase();
      const classes = (el.className || '').toString().toLowerCase();

      const isAddToCart =
        name === 'add' ||
        text.includes('add to cart') ||
        text.includes('add to bag') ||
        text.includes('add') ||
        classes.includes('product-form') ||
        classes.includes('add-to-cart') ||
        el.matches('[data-add-to-cart], [data-product-submit], .product-form__submit, .add-to-cart, .btn--add-to-cart');

      const isCheckout =
        name === 'checkout' ||
        text.includes('checkout') ||
        text.includes('buy it now') ||
        href.includes('/checkout') ||
        classes.includes('shopify-payment-button');

      if (isAddToCart) {
        logAssistantEvent('add_to_cart_clicked', {
          product_url: window.location.pathname,
          button_text: text
        });
      }
      if (isCheckout) {
        logAssistantEvent('checkout_clicked', {
          page_url: window.location.href,
          button_text: text || href
        });
      }
    }, true);

    fetchCatalog();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();